import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, Clock, CheckCircle, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const CateringServices = () => {
  const services = [
    {
      title: 'Breakfast Catering',
      description: 'Start your day right with our delicious breakfast options',
      items: [
        'Continental breakfast platters',
        'Hot breakfast items',
        'Breakfast croissants',
        'Fresh fruit trays',
        'Coffee and beverages'
      ],
      image: 'https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/p61re4tp_477047897_946735490923808_32674825393354609_n.jpg'
    },
    {
      title: 'Lunch Catering',
      description: 'Perfect for meetings, events, and gatherings',
      items: [
        'Sandwich and wrap platters',
        'Slider trays',
        'Salads and sides',
        'Soup options',
        'Dessert selections'
      ],
      image: 'https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/sbwfqi03_472970184_926496479614376_2342581467839661898_n.jpg'
    },
    {
      title: 'Supper Catering',
      description: 'Hearty meals for your evening events',
      items: [
        'Hot entrée selections',
        'Pasta dishes',
        'Meat and protein options',
        'Homemade sides',
        'Desserts and more'
      ],
      image: 'https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/xbouzyz4_471495936_917935190470505_4395504939552086250_n.jpg'
    }
  ];

  const whyChooseUs = [
    {
      icon: CheckCircle,
      title: 'Homemade Quality',
      description: 'Every dish prepared fresh with quality ingredients'
    },
    {
      icon: Users,
      title: 'Any Size Event',
      description: 'From intimate gatherings to large celebrations'
    },
    {
      icon: Calendar,
      title: '7 Days a Week',
      description: 'Catering available every day of the week'
    },
    {
      icon: Clock,
      title: 'Flexible Timing',
      description: 'Breakfast, lunch, or supper - we\'ve got you covered'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-nanny-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/yhtx9eg6_476402542_946735560923801_5528077024555438637_n.jpg" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nanny-dark mb-6 heading-font">Catering Services</h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8">
              Let us handle the food for your next event. Delicious homemade catering available 7 days a week.
            </p>
            <Link to="/order">
              <Button size="lg" className="bg-nanny-maroon hover:bg-nanny-dark text-white text-lg px-8 py-6">
                Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">Why Choose Nanny's Catering?</h2>
            <p className="text-xl text-gray-600">Quality, convenience, and homemade goodness</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-2 border-transparent hover:border-nanny-maroon transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="bg-nanny-cream rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-8 w-8 text-nanny-maroon" />
                    </div>
                    <h3 className="text-xl font-bold text-nanny-dark mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-nanny-cream">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-4 heading-font">Catering Options</h2>
            <p className="text-xl text-gray-600">Breakfast, lunch, and supper catering to fit your needs</p>
          </div>
          <div className="space-y-12 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-full object-cover min-h-[300px]"
                    />
                  </div>
                  <div className={`p-8 flex flex-col justify-center ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="text-3xl font-bold text-nanny-dark mb-4">{service.title}</h3>
                    <p className="text-gray-600 mb-6 text-lg">{service.description}</p>
                    <ul className="space-y-3">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-6 w-6 text-nanny-maroon mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Party Trays Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-nanny-dark mb-6 heading-font">Party Trays & More</h2>
                <p className="text-xl text-gray-700 mb-6">
                  Perfect for any occasion - office parties, family gatherings, celebrations, and more!
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-nanny-maroon mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-nanny-dark">Appetizer Trays</span>
                      <p className="text-gray-600 text-sm">Dips, finger foods, and more</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-nanny-maroon mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-nanny-dark">Dessert Trays</span>
                      <p className="text-gray-600 text-sm">Cookies, brownies, and sweet treats</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-nanny-maroon mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-nanny-dark">Custom Orders</span>
                      <p className="text-gray-600 text-sm">Tell us what you need!</p>
                    </div>
                  </li>
                </ul>
                <Link to="/order">
                  <Button size="lg" className="bg-nanny-maroon hover:bg-nanny-dark text-white">
                    Order Party Trays
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/997cyzj8_473547096_928662969397727_4010279095934232412_n.jpg" 
                  alt="Dessert Tray" 
                  className="rounded-lg shadow-lg w-full h-48 object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_homemade-meals-13/artifacts/yhtx9eg6_476402542_946735560923801_5528077024555438637_n.jpg" 
                  alt="Party Tray" 
                  className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-nanny-maroon text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <Phone className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6 heading-font">Let's Plan Your Event</h2>
            <p className="text-xl mb-8 text-nanny-cream">
              Call us today to discuss your catering needs. We'll work with you to create the perfect menu!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:731-267-9884">
                <Button size="lg" className="bg-white text-nanny-maroon hover:bg-nanny-cream text-lg px-8 py-6 w-full sm:w-auto">
                  Call 731-267-9884
                </Button>
              </a>
              <Link to="/order">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-nanny-maroon text-lg px-8 py-6 w-full sm:w-auto">
                  Order Online
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CateringServices;
